'use strict';

import { APICriteria, APIOptions } from '@api/API/CoreAPI';
import { CommissionStatementResultCollection } from '../../Collections/CommissionStatement/CommissionStatemenResultCollection';
import type { CommissionStatement } from '../../Entities/CommissionStatement/CommissionStatement';
import { CommissionStatementResult } from '../../Entities/CommissionStatement/CommissionStatementResult';
import type { CommissionStatementModuleName } from '../../Model/CommissionStatement/CommissionStatementModule';
import { Utilities } from '../../Utilities/Utilities';
import { CoreRepository } from '../CoreRepository';

export interface CommissionStatementResultSchema {
  commissionStatement?: CommissionStatement;
  data?: any;
  module?: CommissionStatementModuleName;
  dateTimeCreated?: Date;
}

export interface CommissionStatementResultPayload {
  commissionStatement?: number;
  data?: any;
  module?: string;
  dateTimeCreated?: string;
}

/**
 * API response format for commission statement mapping collections
 */
export interface CommissionStatementResultCollectionPayload {
  limit?: number;
  total?: number;
  offset?: number;
  data?: Array<CommissionStatementResultPayload>;
}

/**
 * Searchable fields
 */
export type CommissionStatementResultCriteria = {
  commissionStatement: number;
  module: CommissionStatementModuleName;
};

export type CommissionStatementResultAPIInclude =
  | 'commissionStatement';

/**
 * Criteria & include
 */
export type CommissionStatementResultAPICriteria = APICriteria<CommissionStatementResultCriteria>;

export type CommissionStatementResultAPIOptions = APIOptions<CommissionStatementResultAPICriteria, CommissionStatementResultAPIInclude>;

/**
 * @author Matt Lowden <matt.lowden@medicareprocrm.com>
 */
export class CommissionStatementResultRepository extends CoreRepository {
  protected processData(data: CommissionStatementResultSchema): CommissionStatementResultPayload {
    const payload: CommissionStatementResultPayload = {};

    if (data.hasOwnProperty('module')) {
      payload.module = data.module;
    }

    if (data.hasOwnProperty('data')) {
      payload.data = data.data;
    }

    if (data.commissionStatement) {
      payload.commissionStatement = data.commissionStatement.getId();
    }

    // Convert Date objects to string format
    if (data.hasOwnProperty('dateTimeCreated')) {
      payload.dateTimeCreated = data.dateTimeCreated
        ? Utilities.dateTimeToDateTimeString(data.dateTimeCreated)
        : null;
    }

    return payload;
  }

  public getCommissionStatementsResults(
    userId: number,
    options = {}
  ): Promise<CommissionStatementResultCollection> {
    return this.webAPI.getCommissionStatementsResults(userId, options).then((response) => {
      const collection = new CommissionStatementResultCollection();

      // Hydrate each mapping entity from the API response
      response.data.forEach((collectionItemData) => {
        const collectionItem = new CommissionStatementResult();
        collectionItem.hydrate(collectionItemData);

        collection.addItem(collectionItem);
      });

      // Set pagination metadata
      collection.setOffset(response.offset);
      collection.setLimit(response.limit);
      collection.setTotal(response.total);

      return collection;
    });
  }

  public getCommissionStatementResults(
    userId: number,
    commissionStatementId: number,
    options = {}
  ): Promise<CommissionStatementResultCollection> {
    return this.webAPI
      .getCommissionStatementResults(userId, commissionStatementId, options)
      .then((response) => {
        const collection = new CommissionStatementResultCollection();

        // Hydrate each mapping entity from the API response
        response.data.forEach((collectionItemData) => {
          const collectionItem = new CommissionStatementResult();
          collectionItem.hydrate(collectionItemData);

          collection.addItem(collectionItem);
        });

        // Set pagination metadata
        collection.setOffset(response.offset);
        collection.setLimit(response.limit);
        collection.setTotal(response.total);

        return collection;
      });
  }

  public getCommissionStatementResult(
    userId: number,
    commissionStatementId: number,
    commissionStatementResultId: number,
    options?: CommissionStatementResultAPIOptions
  ): Promise<CommissionStatementResult> {
    return this.webAPI
      .getCommissionStatementResult(
        userId,
        commissionStatementId,
        commissionStatementResultId,
        options
      )
      .then((response) => {
        const item = new CommissionStatementResult();
        item.hydrate(response);

        return item;
      });
  }

  public createCommissionStatementResult(
    userId: number,
    commissionStatementId: number,
    data: CommissionStatementResultSchema
  ): Promise<CommissionStatementResult> {
    const payload = this.processData(data);

    return this.webAPI
      .createCommissionStatementResult(userId, commissionStatementId, payload)
      .then((response) => {
        const item = new CommissionStatementResult();
        item.hydrate(response);

        return item;
      });
  }
}
